import React from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { Query } from '@apollo/client/react/components'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from '@components/error'
import richTextFragment from '@components/renderers/rich-text.fragment'
import { ScrambleLeaderboardQuery } from '@components/leaderboard-scramble'
import { TeamLeaderboardQuery } from '@components/leaderboard-team/team-leaderboard.query'
import { DateTime } from 'luxon'
import LeaderboardPage from './leaderboard-page'
import MatchPlayLeaderboard from './match-play-leaderboard-page'
import { leaderboardFragment } from './leaderboard-fragment'
import ParentLeaderboardPage from './parent-leaderboard-page'

const QUERY = gql`
query Tour($slug: String, $preview: Boolean) {
  tourCollection(limit: 1, preview: $preview, where: {slug: $slug}) {
    items {
      ${leaderboardFragment}
      description {
        ${richTextFragment}
      }
    }
  }
  eventsLandingCollection(limit: 1) {
    items {
      enableStatus
    }
  }
}
`

export default function LeaderboardPageWithData(props) {
  const { query } = props
  const { embed: isEmbedView } = query

  return (
    <Query query={QUERY} variables={query}>
      {({
        loading, error, data,
      }) => {
        if (error) return <Error error={error} />
        if (loading) return <LinearProgress color="secondary" />

        const response = data.tourCollection
          && data.tourCollection.items
          && data.tourCollection.items[0]

        const enableStatus = data.eventsLandingCollection
          && data.eventsLandingCollection.items[0]
          && data.eventsLandingCollection.items[0].enableStatus

        if (!response) {
          return <Error statusCode={404} />
        }

        const {
          tournamentId,
          feedId,
          liveStreamUrl,
          nativeAdTitle,
          nativeAdDescription,
          nativeAdCta,
          nativeAdActive,
          showExternalAd,
          leaderboardPollInterval,
          limit,
          slug,
          eventTournamentIds,
          location,
          startDate,
          endDate,
          course,
          headline,
        } = response

        // Sending an impression event from this component can cause hook errors. Hand the object down instead.
        const trackingEventObject = {
          event: 'view-leaderboard',
          attributes: {
            title: headline,
            tour_name: feedId,
            location: course,
            city: location?.split(', ')[0] || null,
            state: location?.split(', ')[1] || null,
            start_date: DateTime.fromISO(startDate).toFormat('yyyy-LL-dd'),
            end_date: DateTime.fromISO(endDate).toFormat('yyyy-LL-dd'),
          },
        }

        if (response?.format === 'scramble') {
          return (
            <ParentLeaderboardPage {...response} enableStatus={enableStatus} trackingEventObject={trackingEventObject} fullPage>
              <ScrambleLeaderboardQuery
                id={tournamentId}
                feedId={feedId}
                enableScorecard
                enableStatus={enableStatus}
                liveStreamUrl={liveStreamUrl}
                nativeAdTitle={nativeAdTitle}
                nativeAdDescription={nativeAdDescription}
                nativeAdCta={nativeAdCta}
                nativeAdActive={nativeAdActive}
                showExternalAd={showExternalAd}
                leaderboardPollInterval={leaderboardPollInterval}
                limit={limit}
                slug={slug}
                eventTournamentIds={eventTournamentIds}
                fullPage
                {...response}
              />
            </ParentLeaderboardPage>
          )
        }

        if (response?.format === 'team/individual') {
          return (
            <ParentLeaderboardPage {...response} enableStatus={enableStatus} fullPage>
              <TeamLeaderboardQuery
                id={tournamentId}
                feedId={feedId}
                enableScorecard
                enableStatus={enableStatus}
                liveStreamUrl={liveStreamUrl}
                nativeAdTitle={nativeAdTitle}
                nativeAdDescription={nativeAdDescription}
                nativeAdCta={nativeAdCta}
                nativeAdActive={nativeAdActive}
                showExternalAd={showExternalAd}
                leaderboardPollInterval={leaderboardPollInterval}
                limit={limit}
                slug={slug}
                eventTournamentIds={eventTournamentIds}
                fullPage
              />
            </ParentLeaderboardPage>
          )
        }

        if (response?.format === 'match play') {
          return <MatchPlayLeaderboard {...response} fullLeaderboard isEmbedView={!!isEmbedView} trackingEventObject={trackingEventObject} />
        }

        return <LeaderboardPage {...response} enableStatus={enableStatus} trackingEventObject={trackingEventObject} />
      }}
    </Query>
  )
}

LeaderboardPageWithData.propTypes = {
  query: PropTypes.object,
  coach: PropTypes.object,
}
